/**
 * Cuentas de las gráficas (Req. 8.9, 16.4 a 16.6; design.md, 12.4): marcas
 * redondas del eje, el resumen en texto y las franjas que caen en un
 * periodo. Sin React: el componente GraficaSerie solo dibuja.
 */

import { maxima, minima, type FechaLocal } from './dates';
import { redondear } from './numbers';

export interface PuntoGrafica {
  fecha: FechaLocal;
  valor: number;
}

export interface EjeY {
  desde: number;
  hasta: number;
  paso: number;
  marcas: number[];
}

/**
 * Marcas "redondas" para el eje: pasos de 1, 2 o 5 por potencia de 10, así
 * el peso se lee en medios kilos o kilos enteros. Con un solo valor, medio
 * punto arriba y abajo.
 */
export function ejeY(valores: readonly number[], deseadas = 4): EjeY {
  const finitos = valores.filter((v) => Number.isFinite(v));
  if (finitos.length === 0) return { desde: 0, hasta: 1, paso: 1, marcas: [0, 1] };
  let menor = Math.min(...finitos);
  let mayor = Math.max(...finitos);
  if (mayor - menor < 1e-9) {
    menor -= 0.5;
    mayor += 0.5;
  }
  const crudo = (mayor - menor) / deseadas;
  const potencia = 10 ** Math.floor(Math.log10(crudo));
  const paso = [1, 2, 5, 10].map((m) => m * potencia).find((p) => p >= crudo - 1e-12) ?? 10 * potencia;
  const desde = redondear(Math.floor(menor / paso + 1e-9) * paso, 6);
  const hasta = redondear(Math.ceil(mayor / paso - 1e-9) * paso, 6);
  const marcas: number[] = [];
  for (let i = 0; desde + i * paso <= hasta + paso / 1000; i++) marcas.push(redondear(desde + i * paso, 6));
  return { desde, hasta, paso, marcas };
}

/** Decimales para escribir las marcas de un paso: 0.5 → 1, 2 → 0. */
export function decimalesDePaso(paso: number): number {
  if (!(paso > 0)) return 0;
  return Math.max(0, Math.min(3, -Math.floor(Math.log10(paso) + 1e-9)));
}

export interface ResumenDeSerie {
  cantidad: number;
  minimo: PuntoGrafica;
  maximo: PuntoGrafica;
  primero: PuntoGrafica;
  ultimo: PuntoGrafica;
}

/**
 * Lo que dice el resumen en texto (Req. 16.6): cuántos, el mínimo y el máximo
 * con su fecha (el más reciente si se repite) y el primero y el último. null
 * sin puntos.
 */
export function resumenDeSerie(puntos: readonly PuntoGrafica[]): ResumenDeSerie | null {
  if (puntos.length === 0) return null;
  const ordenados = [...puntos].sort((a, b) => a.fecha.localeCompare(b.fecha));
  const primero = ordenados[0] as PuntoGrafica;
  const ultimo = ordenados[ordenados.length - 1] as PuntoGrafica;
  let minimo = primero;
  let maximo = primero;
  for (const p of ordenados) {
    if (p.valor <= minimo.valor) minimo = p;
    if (p.valor >= maximo.valor) maximo = p;
  }
  return { cantidad: ordenados.length, minimo, maximo, primero, ultimo };
}

export type TonoFranja = 'deficit' | 'mantenimiento' | 'superavit';

export interface FranjaGrafica {
  desde: FechaLocal;
  /** null = sin fin. */
  hasta: FechaLocal | null;
  tono: TonoFranja;
  /** "Déficit", "Mantenimiento" o "Superávit". */
  etiqueta: string;
}

export interface FranjaVisible {
  desde: FechaLocal;
  hasta: FechaLocal;
  tono: TonoFranja;
  etiqueta: string;
}

/** Las franjas que caen en el periodo, recortadas a él y en orden (Req. 16.5). */
export function franjasEnPeriodo(
  franjas: readonly FranjaGrafica[],
  desde: FechaLocal,
  hasta: FechaLocal,
): FranjaVisible[] {
  return franjas
    .filter((f) => f.desde <= hasta && (f.hasta === null || f.hasta >= desde))
    .map((f) => ({
      desde: maxima(f.desde, desde),
      hasta: minima(f.hasta ?? hasta, hasta),
      tono: f.tono,
      etiqueta: f.etiqueta,
    }))
    .sort((a, b) => a.desde.localeCompare(b.desde));
}

/** Los puntos de un periodo, en orden. */
export function puntosEnPeriodo(puntos: readonly PuntoGrafica[], desde: FechaLocal, hasta: FechaLocal): PuntoGrafica[] {
  return puntos.filter((p) => p.fecha >= desde && p.fecha <= hasta).sort((a, b) => a.fecha.localeCompare(b.fecha));
}
